import {
  DefaultColorStyle,
  DefaultSizeStyle,
  type TLBaseShape,
  type TLDefaultColorStyle,
  type TLDefaultSizeStyle,
} from "@tldraw/tlschema";
/**
 * The revision cloud: the scalloped outline a reviewer draws around what
 * changed or needs a look. A box shape (w × h from its own x/y) drawn as
 * arcs bulging out along each edge. The bump is page units, so the cloud
 * scales with what it circles, like any other drawn line.
 */
import { T } from "@tldraw/validate";

export interface CloudProps {
  w: number;
  h: number;
  color: TLDefaultColorStyle;
  size: TLDefaultSizeStyle;
}

declare module "@tldraw/tlschema" {
  interface TLGlobalShapePropsMap {
    cloud: CloudProps;
  }
}

export type CloudShape = TLBaseShape<"cloud", CloudProps>;

export const cloudProps: Record<keyof CloudProps, T.Validatable<any>> = {
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,
  color: DefaultColorStyle,
  size: DefaultSizeStyle,
};

/** Bump length by size, in page units: fine loops, as a revision cloud is
 *  drawn on a sheet (a plane is 1000 units tall where it was made). */
export const CLOUD_BUMP: Record<TLDefaultSizeStyle, number> = {
  s: 8,
  m: 12,
  l: 18,
  xl: 28,
};

/** An arc's radius over its chord: nearly a half circle (0.5). */
const ARC = 0.55;

type Point = { x: number; y: number };

/** One scallop: an arc of radius `r` from `from` to `to`, bulging `out`. */
interface CloudArc {
  from: Point;
  to: Point;
  r: number;
  out: Point;
}

/**
 * The cloud's scallops. They walk the box clockwise (Y-down); each edge gets
 * whole bumps close to `bump` long, so corners land on a cusp.
 */
function cloudArcs(w: number, h: number, bump: number): CloudArc[] {
  const corners = [
    { x: 0, y: 0 },
    { x: w, y: 0 },
    { x: w, y: h },
    { x: 0, y: h },
  ];
  return corners.flatMap((a, i) => {
    const b = corners[(i + 1) % 4]!;
    const length = Math.hypot(b.x - a.x, b.y - a.y);
    const n = Math.max(1, Math.round(length / bump));
    const at = (t: number) => ({
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    });
    // Clockwise in Y-down, outward is the edge direction turned left.
    const out = { x: (b.y - a.y) / length, y: -(b.x - a.x) / length };
    return Array.from({ length: n }, (_, k) => ({
      from: at(k / n),
      to: at((k + 1) / n),
      r: (length / n) * ARC,
      out,
    }));
  });
}

/** The cloud's outline as an SVG path: with the sweep flag set, every arc
 *  bulges outward. */
export function cloudPath(w: number, h: number, bump: number): string {
  const arcs = cloudArcs(w, h, bump).map(
    ({ to, r }) => `A ${r} ${r} 0 0 1 ${to.x} ${to.y}`,
  );
  return `M 0 0 ${arcs.join(" ")} Z`;
}

/** The same outline as points, `steps` to a scallop: what it is hit-tested
 *  against, so a click on a scallop's crown finds the cloud. */
export function cloudOutline(
  w: number,
  h: number,
  bump: number,
  steps = 6,
): Point[] {
  return cloudArcs(w, h, bump).flatMap(({ from, to, r, out }) => {
    const half = Math.hypot(to.x - from.x, to.y - from.y) / 2;
    const inset = Math.sqrt(r * r - half * half);
    const centre = {
      x: (from.x + to.x) / 2 - out.x * inset,
      y: (from.y + to.y) / 2 - out.y * inset,
    };
    const start = Math.atan2(from.y - centre.y, from.x - centre.x);
    const end = Math.atan2(to.y - centre.y, to.x - centre.x);
    // The short way round, which is the way through the crown.
    const sweep = ((end - start + 3 * Math.PI) % (2 * Math.PI)) - Math.PI;
    return Array.from({ length: steps }, (_, k) => {
      const angle = start + (sweep * k) / steps;
      return {
        x: centre.x + r * Math.cos(angle),
        y: centre.y + r * Math.sin(angle),
      };
    });
  });
}
